import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { date } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Pencil } from "lucide-react";
import { Badge, FileLink, UploadButton, AddButton } from "@/components/hr/HrBits";

const SUGGEST = ["Safe Pass", "Manual Handling", "Welding Ticket", "First Aid", "Forklift Licence", "Working at Height", "MEWP / IPAF"];

function CertDialog({ userId, cert, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", issue_date: "", expiry_date: "", file: null });
  useEffect(() => { if (open) setF(cert ? { name: cert.name, issue_date: cert.issue_date, expiry_date: cert.expiry_date, file: cert.file } : { name: "", issue_date: "", expiry_date: "", file: null }); }, [open, cert]);
  const save = async () => {
    try {
      if (cert) await api.patch(`/hr/certs/${cert.id}`, f);
      else await api.post("/hr/certs", { ...f, user_id: userId });
      toast.success("Cert saved"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading uppercase">{cert ? "Edit cert" : "Add cert"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Cert name</Label><Input list="cert-suggest" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cert-name" className="h-11" />
            <datalist id="cert-suggest">{SUGGEST.map((s) => <option key={s} value={s} />)}</datalist></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Issued</Label><Input type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} data-testid="cert-issue" className="h-11" /></div>
            <div><Label className="text-xs">Expires</Label><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="cert-expiry" className="h-11" /></div>
          </div>
          <UploadButton value={f.file} onChange={(file) => setF({ ...f, file })} testid="cert-file" />
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name} data-testid="cert-save" className="w-full h-11 font-bold uppercase">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CertsPanel({ userId, hr }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/hr/certs", { params: { user_id: userId } }).then((r) => setRows(r.data)); }, [userId]);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/hr/certs/${c.id}`); toast.success("Deleted"); load();
  };
  return (
    <div>
      <div className="flex justify-end mb-3"><CertDialog userId={userId} onDone={load} trigger={<AddButton label="Add cert" testid="cert-add" />} /></div>
      {rows.length === 0 ? <div className="text-center py-8 text-sm text-muted-foreground">No certs recorded yet.</div> : (
        <div className="card-industrial divide-y divide-border" data-testid="certs-list">
          {rows.map((c) => (
            <div key={c.id} className="p-4 flex items-center justify-between gap-3" data-testid={`cert-row-${c.id}`}>
              <div>
                <div className="flex items-center gap-2"><span className="font-bold">{c.name}</span><Badge status={c.status} testid={`cert-status-${c.id}`} /></div>
                <div className="text-xs text-muted-foreground mt-1">Issued {date(c.issue_date)} · Expires {date(c.expiry_date)}</div>
                <FileLink file={c.file} />
              </div>
              {hr && <div className="flex gap-1">
                <CertDialog cert={c} onDone={load} trigger={<Button size="icon" variant="ghost" data-testid={`cert-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>} />
                <Button size="icon" variant="ghost" onClick={() => del(c)} data-testid={`cert-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
